import {clearForItem} from "#user-info-store-package/user-info-actions.ts";
import userInfoStore from "#user-info-store-package/user-info-store.ts";

import {
    getModalUrlForContent,
    getModalUrlForTestOrQuiz,
} from "../utils/modal-utils.ts";
import {getSourceId} from "../utils/quiz-utils.ts";

import type {Action, ModalItem, State} from "./modal-navigator-types.ts";

const initialState = {
    modalData: null,
    focusId: "",
} as const;

const uniqueId = (item: ModalItem) => {
    if (item.kind === "InterspersedQuiz") {
        return getSourceId(item);
    }
    return item.nodeUrl;
};

export const getNextItem = (state: State): null | ModalItem => {
    const modalData = state.modalData;

    if (modalData) {
        const currentItem = modalData.currentItem;
        const index = modalData.items.findIndex(
            (item) => uniqueId(item) === uniqueId(currentItem),
        );

        if (index !== -1) {
            return modalData.items[index + 1];
        }
    }

    return null;
};

export const getPrevItem = (state: State): null | ModalItem => {
    const modalData = state.modalData;

    if (modalData) {
        const currentItem = modalData.currentItem;
        const index = modalData.items.findIndex(
            (item) => uniqueId(item) === uniqueId(currentItem),
        );

        if (index !== -1) {
            return modalData.items[index - 1];
        }
    }

    return null;
};

const getNextOrPrevItem = (state: State, action: Action) =>
    action.type === "NextItem" ? getNextItem(state) : getPrevItem(state);

export const getCurrentItem = (state: State): null | ModalItem => {
    const modalData = state.modalData;
    return modalData ? modalData.currentItem : null;
};

function reducer(state: State = initialState, action: Action): State {
    const modalData = state.modalData;
    switch (action.type) {
        case "OpenItem":
            return {
                ...state,
                modalData: {
                    ...action.modalData,
                },
                focusId: action.focusId,
            };
        case "CloseItem":
            return {
                modalData: null,
            };

        case "PrevItem":
        case "NextItem": {
            const newItem = getNextOrPrevItem(state, action);

            if (newItem) {
                // HACK(jared): This is terrible and we need to merge these
                // two stores.
                const currentTopic = userInfoStore.getState().currentTopic;
                if (
                    currentTopic &&
                    newItem.kind !== "InterspersedQuiz" &&
                    newItem.kind !== "Article"
                ) {
                    userInfoStore.dispatch(clearForItem(newItem, currentTopic));
                }

                const newState: State = {
                    ...state,
                    /**
                     * TODO(somewhatabstract, JIRA-XXX): `state.modalData` is
                     * maybe typed, so it may not be there, and if it isn't
                     * then this spread won't create a complete `modalData`
                     * on the new state.
                     *
                     * Easiest fix maybe to use Partial<> in the state and then
                     * cope with values being missing elsewhere or to add
                     * a check in this case that ensures we don't get here
                     * with a missing `modalData` on the state.
                     */
                    // @ts-expect-error - TS2322 - Type '{ currentItem: Video | Exercise | Article | Scratchpad | Challenge | Talkthrough | Project | Interactive | InterspersedQuizCommon; items?: readonly ModalItem[] | undefined; referrer?: string | undefined; shouldAutoplay?: boolean | undefined; }' is not assignable to type 'ModalData'.
                    modalData: {
                        ...modalData,
                        currentItem: newItem,
                    },
                };

                // If this modal is opened in student assignments table, we
                // don't want to change url so return the new state early.
                if (
                    modalData &&
                    modalData.referrer === "student-assignments-table"
                ) {
                    return newState;
                }
                if (
                    // We also don't want to change url in SAT
                    modalData &&
                    modalData.referrer === "sat-related-content-widget"
                ) {
                    return newState;
                }

                let newUrl;
                if (newItem.kind === "InterspersedQuiz") {
                    newUrl = getModalUrlForTestOrQuiz(
                        {
                            type: newItem.type,
                            slug: newItem.slug,
                        },
                        action.location,
                    );
                } else {
                    newUrl = getModalUrlForContent(newItem, action.location);
                }

                window.history.replaceState(newState, newItem.title, newUrl);
                return newState;
            }
            return state;
        }

        default:
            return state;
    }
}

export default reducer;
